import {
  type EnvironmentId,
  type EventId,
  MessageId,
  type OrchestrationTurnInspection,
  ThreadId,
  type TurnId,
} from "@t3tools/contracts";
import { Trans, useLingui } from "@lingui/react/macro";
import { type TimelineScrollHandle } from "./chat/useTimelineScroll";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { scopedThreadKey, scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime";

import { ExpandedImageDialog } from "./chat/ExpandedImageDialog";
import { type ExpandedImagePreview } from "./chat/ExpandedImagePreview";
import { MessagesTimeline, TimelineEmptyMessage } from "./chat/MessagesTimeline";
import { TimelineScrollToBottomPill, useTimelineAutoScroll } from "./chat/timelineAutoScroll";
import {
  fetchThreadActivityPayload,
  fetchTurnInspection,
  loadOlderThreadActivities,
  loadOlderThreadMessages,
  retainThreadDetailSubscription,
} from "../environments/runtime/service";
import { useEnvironmentServerConfig } from "../hooks/useEnvironmentServerConfig";
import { useTheme } from "../hooks/useTheme";
import { projectArtifactsRoute } from "../markdown-links";
import {
  deriveActiveWorkStartedAt,
  derivePhase,
  deriveTimelineEntries,
  deriveWorkLogEntries,
  isLatestTurnSettled,
} from "../session-logic";
import { useStore, selectEnvironmentState, selectProjectByRef, selectThreadByRef } from "../store";
import { type ChatMessage } from "../types";
import { useInspectAccess } from "./settings/useInspectAccess";

export function SubAgentThreadView({
  environmentId,
  threadId,
  prompt,
}: {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  prompt?: string | undefined;
}) {
  useEffect(
    () => retainThreadDetailSubscription(environmentId, threadId),
    [environmentId, threadId],
  );

  const threadRef = scopeThreadRef(environmentId, threadId);
  const thread = useStore((state) => selectThreadByRef(state, threadRef));
  const project = useStore((state) =>
    thread
      ? selectProjectByRef(state, scopeProjectRef(thread.environmentId, thread.projectId))
      : undefined,
  );
  const messageWindow = useStore((state) =>
    thread
      ? selectEnvironmentState(state, thread.environmentId).messageWindowByThreadId[thread.id]
      : undefined,
  );
  const serverConfig = useEnvironmentServerConfig(thread?.environmentId);
  const { resolvedTheme } = useTheme();
  const [expandedImage, setExpandedImage] = useState<ExpandedImagePreview | null>(null);

  const listRef = useRef<TimelineScrollHandle | null>(null);
  const { showScrollToBottom, onIsAtEndChange, scrollToEnd, resetToEnd } =
    useTimelineAutoScroll(listRef);
  useEffect(() => {
    resetToEnd();
  }, [resetToEnd]);

  const promptMessage = useMemo<ChatMessage | null>(() => {
    if (!thread || !prompt || prompt.trim().length === 0) {
      return null;
    }
    return {
      id: MessageId.make(`subagent-prompt:${thread.id}`),
      role: "user",
      text: prompt,
      createdAt: thread.createdAt,
      streaming: false,
    };
  }, [prompt, thread]);

  const { i18n } = useLingui();
  const { hasAccess: inspect } = useInspectAccess();
  const latestTurn = thread?.latestTurn ?? null;
  const session = thread?.session ?? null;
  const isWorking = derivePhase(session) === "running";
  const latestTurnSettled = isLatestTurnSettled(latestTurn, session);
  const activeWorkStartedAt = deriveActiveWorkStartedAt(latestTurn, session, null);

  const timelineEntries = useMemo(() => {
    if (!thread) {
      return [];
    }
    const messages = promptMessage ? [promptMessage, ...thread.messages] : thread.messages;
    return deriveTimelineEntries(
      messages,
      deriveWorkLogEntries(
        thread.activities,
        latestTurn?.turnId ?? undefined,
        latestTurnSettled,
        i18n.locale,
        inspect,
      ),
    );
  }, [i18n.locale, inspect, latestTurn?.turnId, latestTurnSettled, promptMessage, thread]);

  const handleLoadOlder = useCallback(() => {
    if (thread) {
      loadOlderThreadMessages(thread.environmentId, thread.id);
      loadOlderThreadActivities(thread.environmentId, thread.id);
    }
  }, [thread]);
  const handleLoadActivityPayload = useCallback(
    (activityId: string): Promise<unknown> => {
      if (!thread) {
        return Promise.resolve(undefined);
      }
      return fetchThreadActivityPayload(thread.environmentId, thread.id, activityId as EventId);
    },
    [thread],
  );
  const handleLoadTurnInspection = useCallback(
    (turnId: TurnId): Promise<OrchestrationTurnInspection | undefined> => {
      if (!thread) {
        return Promise.resolve(undefined);
      }
      return fetchTurnInspection(thread.environmentId, thread.id, turnId);
    },
    [thread],
  );
  const handleImageExpand = useCallback((preview: ExpandedImagePreview) => {
    setExpandedImage(preview);
  }, []);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {thread ? (
        <MessagesTimeline
          isWorking={isWorking}
          activeTurnInProgress={!latestTurnSettled}
          activeTurnId={latestTurn?.turnId ?? null}
          activeTurnStartedAt={activeWorkStartedAt}
          timelineRef={listRef}
          timelineEntries={timelineEntries}
          routeThreadKey={scopedThreadKey(threadRef)}
          onImageExpand={handleImageExpand}
          activeThreadEnvironmentId={thread.environmentId}
          timelineThreadId={thread.id}
          markdownCwd={project?.cwd ?? undefined}
          markdownArtifactsRoute={projectArtifactsRoute(serverConfig?.artifactsDir, project?.cwd)}
          resolvedTheme={resolvedTheme}
          workspaceRoot={project?.cwd ?? undefined}
          onIsAtEndChange={onIsAtEndChange}
          hasMoreBefore={messageWindow?.hasMoreBefore ?? false}
          loadingOlder={messageWindow?.loadingOlder ?? false}
          loading={messageWindow === undefined}
          onLoadOlder={handleLoadOlder}
          onLoadActivityPayload={handleLoadActivityPayload}
          onLoadTurnInspection={handleLoadTurnInspection}
          emptyState={
            <TimelineEmptyMessage>
              <Trans>No transcript yet</Trans>
            </TimelineEmptyMessage>
          }
        />
      ) : (
        <TimelineEmptyMessage>
          <Trans>No transcript yet</Trans>
        </TimelineEmptyMessage>
      )}
      {thread && showScrollToBottom && (
        <TimelineScrollToBottomPill onClick={() => scrollToEnd(true)} />
      )}
      {expandedImage && (
        <ExpandedImageDialog preview={expandedImage} onClose={() => setExpandedImage(null)} />
      )}
    </div>
  );
}
